import { useEffect, useId, useRef, useState } from 'react';
import classNames from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';
import { useStatsigClient } from '@statsig/react-bindings';

import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import { exitSuggestion } from '@tiptap/suggestion';
import { PluginKey } from '@tiptap/pm/state';
import Bold from '@tiptap/extension-bold';
import Document from '@tiptap/extension-document';
import Italic from '@tiptap/extension-italic';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import TiptapMention from '@tiptap/extension-mention';
import Underline from '@tiptap/extension-underline';

import {
  BoldIcon,
  CloseBoldIcon,
  CloseIcon,
  ExclamationTriangleIcon,
  ImageIcon,
  ItalicIcon,
  RepromptIcon,
  SerifXIcon,
  UnderlineIcon,
} from '../../icons';

import ErrorText from '../ErrorText';
import MentionList from './components/MentionList';
import OverlayTriggerTooltip from '../OverlayTriggerTooltip';

import { stripHtml } from '@utilities';
import { createMentionSuggestionConfig, createSuggestionRenderer } from './utilities/helpers';

import { GATES } from '@constants';

import styles from './_index.module.scss';

const VARIABLE_TRIGGER = '[';
const PREV_ANSWER_TRIGGER = '{';

const variableSuggestionPluginKey = new PluginKey('variableSuggestion');
const prevAnswerSuggestionPluginKey = new PluginKey('prevAnswerSuggestion');

export interface RichTextInputProps {
  autoFocus?: boolean;
  className?: string;
  error?: string;
  hasConceptName?: boolean;
  hasImageError?: boolean;
  id?: string;
  imageControlsLeadingContent?: React.ReactNode;
  imageControlsPlacement?: 'below' | 'toolbar';
  imageUrl?: string;
  isDisabled?: boolean;
  isExpanded?: boolean;
  isImageLoading?: boolean;
  isImageUploadDisabled?: boolean;
  label?: React.ReactNode;
  maxImageSize?: number;
  mentions?: MentionConfig[];
  onChange?: (value: string) => void;
  onImageRemove?: () => void;
  onImageUpload?: (file: File) => Promise<void>;
  onPaste?: (event: ClipboardEvent, value: string) => void;
  placeholder?: string;
  setHasImageError?: (hasError: boolean) => void;
  suggestions?: Record<string, unknown>;
  value: string;
}

type MentionConfig = {
  data?: MentionDataItem[];
  displayTransform?: (id: string, display: string) => string;
  icon?: React.ReactNode;
  key?: string;
  label?: string;
  markup?: string;
  trigger: string;
};
type MentionDataItem = {
  display: string;
  id: string;
};

const RichTextInput = ({
  autoFocus,
  className,
  error,
  hasConceptName = false,
  hasImageError,
  id,
  imageControlsLeadingContent,
  imageControlsPlacement = 'toolbar',
  imageUrl,
  isDisabled,
  isExpanded,
  isImageLoading,
  isImageUploadDisabled,
  label,
  maxImageSize,
  mentions = [],
  onChange = () => {},
  onImageRemove,
  onImageUpload,
  onPaste,
  placeholder,
  setHasImageError,
  suggestions = {},
  value = '',
}: RichTextInputProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const fileInputId = `${inputId}-file-input`;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageErrorText, setImageErrorText] = useState<string>('');
  const [file, setFile] = useState<File | undefined>(undefined);
  const [isFocused, setIsFocused] = useState(false);
  const { client } = useStatsigClient();
  const canShowRichTextFormatting = client.checkGate(GATES.RICH_TEXT_FORMATTING);
  const shouldShowImageControlInToolbar = imageControlsPlacement === 'toolbar';
  const shouldShowImageControlBelow = imageControlsPlacement === 'below';

  const getMentionConfigByTrigger = (trigger: string) => {
    return mentions.find((mentionConfig) => mentionConfig.trigger === trigger);
  };

  const getMentionItems = (trigger: string, query: string) => {
    const mentionConfig = getMentionConfigByTrigger(trigger);
    if (!mentionConfig?.data) return [];

    const normalizedQuery = query.toLowerCase();

    return mentionConfig.data
      .filter((item) => {
        const displayText = stripHtml((item.display || '').toLowerCase());
        const idText = stripHtml((item.id || '').toLowerCase());
        return displayText.includes(normalizedQuery) || idText.includes(normalizedQuery);
      })
      .map((item) => ({
        id: item.id,
        label: stripHtml(item.display),
        trigger,
      }));
  };

  const renderMentionText = (trigger: string, label: string) => {
    if (trigger === VARIABLE_TRIGGER) return `[${label.toLowerCase()}]`;
    if (trigger === PREV_ANSWER_TRIGGER) return `{${label}}`;
    return label;
  };

  const suggestionRenderer = createSuggestionRenderer({ MentionList, styles, suggestions });

  const editor = useEditor({
    content: value || '',
    editorProps: {
      handlePaste(view, event) {
        if (!onPaste) return false;

        const text = event.clipboardData?.getData('text/plain') || '';
        onPaste(event, text);
        return event.defaultPrevented;
      },
    },
    extensions: [
      ...(canShowRichTextFormatting ? [Bold, Italic, Underline] : []),
      Document,
      Paragraph,
      Text,
      Placeholder.configure({
        placeholder: placeholder || '',
      }),
      TiptapMention.configure({
        HTMLAttributes: {
          class: 'rich-text-input-formatting-mention',
        },
        renderHTML({ node }) {
          const trigger = node.attrs.mentionSuggestionChar || VARIABLE_TRIGGER;
          const label = node.attrs.id || node.attrs.label || '';

          return [
            'span',
            {
              class: 'rich-text-input-formatting-mention',
              'data-mention-id': node.attrs.id,
              'data-mention-trigger': trigger,
            },
            renderMentionText(trigger, label),
          ];
        },
        suggestions: [
          createMentionSuggestionConfig({
            getMentionItems,
            pluginKey: variableSuggestionPluginKey,
            render: suggestionRenderer,
            trigger: VARIABLE_TRIGGER,
          }),
          createMentionSuggestionConfig({
            getMentionItems,
            pluginKey: prevAnswerSuggestionPluginKey,
            render: suggestionRenderer,
            trigger: PREV_ANSWER_TRIGGER,
          }),
        ],
      }),
    ],
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isBold: editor.isActive('bold'),
      isItalic: editor.isActive('italic'),
      isUnderline: editor.isActive('underline'),
    }),
  });

  useEffect(() => {
    if (autoFocus && editor) {
      editor.commands.focus(undefined, { scrollIntoView: false });
    }
  }, [autoFocus, editor]);

  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!isDisabled);
  }, [editor, isDisabled]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0];

      if (maxImageSize && file.size > maxImageSize) {
        setHasImageError?.(true);
        setImageErrorText(`File size exceeds ${maxImageSize / (1024 * 1024)}MB limit`);
        return;
      }

      if (onImageUpload) {
        try {
          await onImageUpload(file);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          return;
        }
      }
      setFile(file);
    }
  };

  const handleFileRemove = () => {
    if (onImageRemove) {
      onImageRemove();
    }
    setFile(undefined);
  };

  const handleAddImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleKeyDown = ({ code }: React.KeyboardEvent<HTMLInputElement>) => {
    if (isDisabled) {
      return;
    }
    if (code === 'Tab') {
      setIsFocused(false);
    }
  };

  const handleAddConceptDetails = () => {
    if (!editor) return;
    const { from } = editor.state.selection;
    const textBefore = from > 0 ? editor.state.doc.textBetween(from - 1, from) : '';
    const needSpace = textBefore && !/\s/.test(textBefore);

    editor
      .chain()
      .focus()
      .insertContent([
        ...(needSpace ? [{ text: ' ', type: 'text' }] : []),
        {
          attrs: {
            id: 'concept_name',
            label: 'concept_name',
            mentionSuggestionChar: VARIABLE_TRIGGER,
          },
          type: 'mention',
        },
      ])
      .run();
  };

  const getMentionPluginKey = (trigger: string) => {
    if (trigger === VARIABLE_TRIGGER) return variableSuggestionPluginKey;
    if (trigger === PREV_ANSWER_TRIGGER) return prevAnswerSuggestionPluginKey;
    return undefined;
  };

  const handleMentionButtonClick = (mentionConfig: MentionConfig) => {
    if (!editor) return;

    const pluginKey = getMentionPluginKey(mentionConfig.trigger);

    if (pluginKey) {
      exitSuggestion(editor.view, pluginKey);
    }

    const { from } = editor.state.selection;
    const textBefore = from > 0 ? editor.state.doc.textBetween(from - 1, from) : '';
    const needSpace = textBefore && !/\s/.test(textBefore);

    editor
      .chain()
      .focus()
      .insertContent((needSpace ? ' ' : '') + mentionConfig.trigger)
      .run();
    return;
  };

  return (
    <div
      className={classNames(styles['rich-text-input'], className, {
        [styles['is-disabled']]: isDisabled,
      })}
      onFocus={() => setIsFocused(true)}
      onKeyDown={handleKeyDown}
    >
      {label && (
        <label
          className={styles['rich-text-input-label']}
          htmlFor={inputId}
          id={`${inputId}-label`}
        >
          {label}
        </label>
      )}
      <OutsideClickHandler onOutsideClick={() => !editor?.isFocused && setIsFocused(false)}>
        <div
          className={classNames(styles['rich-text-input-container'], {
            [styles['is-focused']]: isFocused,
          })}
        >
          {(isExpanded || isFocused) && (
            <>
              <div className={styles['rich-text-input-tools']}>
                {canShowRichTextFormatting && (
                  <div className={styles['rich-text-input-formatting']}>
                    <OverlayTriggerTooltip content={isDisabled ? null : 'Bold'}>
                      <button
                        aria-label="Bold"
                        className={classNames(styles['rich-text-input-formatting-button'], {
                          [styles['is-active']]: editorState?.isBold,
                        })}
                        onClick={(e) => {
                          e.preventDefault();
                          editor?.chain().focus().toggleBold().run();
                        }}
                        type="button"
                      >
                        <BoldIcon />
                      </button>
                    </OverlayTriggerTooltip>
                    <OverlayTriggerTooltip content={isDisabled ? null : 'Italic'}>
                      <button
                        aria-label="Italic"
                        className={classNames(styles['rich-text-input-formatting-button'], {
                          [styles['is-active']]: editorState?.isItalic,
                        })}
                        onClick={(e) => {
                          e.preventDefault();
                          editor?.chain().focus().toggleItalic().run();
                        }}
                        type="button"
                      >
                        <ItalicIcon />
                      </button>
                    </OverlayTriggerTooltip>
                    <OverlayTriggerTooltip content={isDisabled ? null : 'Underline'}>
                      <button
                        aria-label="Underline"
                        className={classNames(styles['rich-text-input-formatting-button'], {
                          [styles['is-active']]: editorState?.isUnderline,
                        })}
                        onClick={(e) => {
                          e.preventDefault();
                          editor?.chain().focus().toggleUnderline().run();
                        }}
                        type="button"
                      >
                        <UnderlineIcon />
                      </button>
                    </OverlayTriggerTooltip>
                  </div>
                )}
                {hasConceptName && (
                  <div className={styles['rich-text-concept-tools']}>
                    {hasConceptName && (
                      <OverlayTriggerTooltip content={isDisabled ? null : 'Insert Concept Name'}>
                        <button
                          aria-label="Insert Concept Name"
                          disabled={isDisabled}
                          onClick={handleAddConceptDetails}
                        >
                          <SerifXIcon />
                          <span>Concept Name</span>
                        </button>
                      </OverlayTriggerTooltip>
                    )}
                  </div>
                )}
                {shouldShowImageControlInToolbar && (
                  <OverlayTriggerTooltip
                    content={
                      isDisabled
                        ? null
                        : isImageUploadDisabled
                          ? 'Only one image type allowed per question.'
                          : 'Add Image'
                    }
                  >
                    <button
                      aria-label="Add Image"
                      disabled={isDisabled || isImageUploadDisabled}
                      onClick={handleAddImageClick}
                      type="button"
                    >
                      <ImageIcon />
                      <span>Add an Image</span>
                    </button>
                    <input
                      accept="image/gif, image/jpeg, image/png"
                      aria-hidden
                      className={styles['rich-text-input-file']}
                      disabled={isDisabled || isImageUploadDisabled}
                      id={fileInputId}
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      tabIndex={-1}
                      type="file"
                    />
                  </OverlayTriggerTooltip>
                )}
                {mentions.map((mentionConfig, index) => (
                  <OverlayTriggerTooltip
                    content={isDisabled ? null : mentionConfig.label}
                    key={index}
                  >
                    <button
                      aria-label={mentionConfig.label}
                      disabled={isDisabled}
                      onClick={() => handleMentionButtonClick(mentionConfig)}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {mentionConfig.icon}
                      <span>{mentionConfig.label}</span>
                    </button>
                  </OverlayTriggerTooltip>
                ))}
              </div>
              <div className={styles['rich-text-input-divider']} />
            </>
          )}
          <div className={styles['rich-text-input-editor']}>
            <EditorContent
              aria-invalid={Boolean(error)}
              aria-labelledby={`${inputId}-label`}
              editor={editor}
              id={inputId}
            />
          </div>

          {shouldShowImageControlBelow && (
            <div className={styles['rich-text-input-image-controls-container']}>
              {imageControlsLeadingContent}
              <OverlayTriggerTooltip
                content={
                  isDisabled
                    ? null
                    : isImageUploadDisabled
                      ? 'Only one image type allowed per question.'
                      : 'Add Image'
                }
              >
                <button
                  aria-label="Add Image"
                  className={styles['rich-text-input-add-image-bottom']}
                  disabled={isDisabled || isImageUploadDisabled}
                  onClick={handleAddImageClick}
                  type="button"
                >
                  <ImageIcon />
                  <span>Add an Image</span>
                </button>
                <input
                  accept="image/gif, image/jpeg, image/png"
                  aria-hidden
                  className={styles['rich-text-input-file']}
                  disabled={isDisabled || isImageUploadDisabled}
                  id={fileInputId}
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  tabIndex={-1}
                  type="file"
                />
              </OverlayTriggerTooltip>
            </div>
          )}

          {(hasImageError || isImageLoading || file || imageUrl) && (
            <div className={styles['rich-text-input-image-preview-container']}>
              {hasImageError && (
                <div className={styles['rich-text-input-image-error']}>
                  <div className={styles['rich-text-input-image-error-text']}>
                    <ExclamationTriangleIcon /> Image upload failed:{' '}
                    {imageErrorText || 'please try again'}.
                  </div>
                  <div
                    className={styles['rich-text-input-image-error-close-icon']}
                    onClick={() => {
                      setHasImageError?.(false);
                      setImageErrorText('');
                    }}
                  >
                    <CloseBoldIcon />
                  </div>
                </div>
              )}
              {isImageLoading && (
                <div className={styles['rich-text-input-image-loading']}>
                  <RepromptIcon /> Image is uploading...
                </div>
              )}
              {!isImageLoading && !hasImageError && (file || imageUrl) && (
                <div className={styles['rich-text-input-image-preview']}>
                  <img alt="Uploaded File" src={file ? URL.createObjectURL(file) : imageUrl} />
                  <div className={styles['rich-text-input-remove-image-container']}>
                    <OverlayTriggerTooltip content="Remove Image">
                      <div
                        className={styles['rich-text-input-remove-image-button']}
                        onClick={handleFileRemove}
                      >
                        <CloseIcon className={styles['rich-text-input-remove-image-icon']} />
                      </div>
                    </OverlayTriggerTooltip>
                  </div>
                  <div className={styles['rich-text-input-image-preview-overlay']} />
                </div>
              )}
            </div>
          )}
        </div>
      </OutsideClickHandler>
      {error && <ErrorText className={styles['rich-text-input-error']}>{error}</ErrorText>}
    </div>
  );
};

export default RichTextInput;
